import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import {
  searchCallPools,
  createCallPool,
  updateCallPool,
  getCallPoolClinics,
  updatePoolClinicsAfterSave,
  selectCallPoolItems,
  selectCallPoolTotalCount,
  selectCallPoolSaving,
  selectPoolClinicsMap,
  selectPoolClinicsLoading,
  callPoolsStatus,
} from '../../../store/reducers/CallPoolSlice';
import { getClinics, selectClinics } from '../../../store/reducers/ClinicSlice';
import { useDebounce } from '../../shared/useDebounce';
import { Clinic } from '../../../models/MedVox/Clinic';
import {
  CallPoolWithClinics,
  CreateCallPoolRequest,
  EditableRow,
  EditValues,
} from '../../../models/Settings/CallPool';

const DEFAULT_PAGE_SIZE = 20;

export const useCallPoolSettings = () => {
  const dispatch = useAppDispatch();

  const callPoolItems = useAppSelector(selectCallPoolItems);
  const totalItemsCount = useAppSelector(selectCallPoolTotalCount);
  const saving = useAppSelector(selectCallPoolSaving);
  const status = useAppSelector(callPoolsStatus);
  const poolClinicsMap = useAppSelector(selectPoolClinicsMap);
  const poolClinicsLoading = useAppSelector(selectPoolClinicsLoading);
  const clinics = useAppSelector(selectClinics);

  // Search & pagination
  const [searchValue, setSearchValue] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const debouncedSearch = useDebounce(searchValue, 500);

  // Modal editing
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<EditValues | null>(null);

  // Create pool modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getClinics());
  }, [dispatch]);

  // Search + pagination effect (covers initial load + subsequent changes)
  const prevSearchRef = useRef<string>('');

  useEffect(() => {
    const isSearchChanged = prevSearchRef.current !== debouncedSearch;
    prevSearchRef.current = debouncedSearch;

    if (isSearchChanged) {
      setPageIndex(0);
    }

    dispatch(
      searchCallPools({
        TextSearch: debouncedSearch,
        PageIndex: isSearchChanged ? 0 : pageIndex,
        PageSize: pageSize,
      }),
    );
  }, [debouncedSearch, pageIndex, pageSize, dispatch]);

  // Fetch clinics for each loaded pool via /api/callpool/{id}/clinic
  // Sequential requests with delay to avoid overwhelming the backend
  const poolIdsRef = useRef<string[]>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchClinicsSequentially = useCallback(
    async (ids: string[]) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      for (const id of ids) {
        if (controller.signal.aborted) return;
        dispatch(getCallPoolClinics(id));
        if (id !== ids[ids.length - 1]) {
          await new Promise<void>((resolve) => {
            const timer = setTimeout(resolve, 150);
            controller.signal.addEventListener('abort', () => {
              clearTimeout(timer);
              resolve();
            });
          });
        }
      }
    },
    [dispatch],
  );

  useEffect(() => {
    const currentIds = callPoolItems.map((p) => p.id);
    const prevIds = poolIdsRef.current;
    const hasNewPools = currentIds.some((id) => !prevIds.includes(id));

    if (hasNewPools || prevIds.length === 0) {
      const idsToFetch = hasNewPools
        ? currentIds.filter((id) => !prevIds.includes(id))
        : currentIds;
      fetchClinicsSequentially(idsToFetch);
    }

    poolIdsRef.current = currentIds;
  }, [callPoolItems, fetchClinicsSequentially]);

  // Enrich pool items with per-pool clinics from dedicated endpoint
  const enrichedItems = useMemo<CallPoolWithClinics[]>(
    () =>
      callPoolItems.map((pool) => ({
        ...pool,
        clinics: poolClinicsMap[pool.id] ?? pool.clinics,
      })),
    [callPoolItems, poolClinicsMap],
  );

  // Derive all available clinics from per-pool data
  const allClinics = useMemo<Clinic[]>(() => {
    const clinicMap = new Map<string, Clinic>();
    Object.values(poolClinicsMap).forEach((clinics) => {
      clinics.forEach((clinic) => {
        clinicMap.set(clinic.id, clinic);
      });
    });
    return Array.from(clinicMap.values());
  }, [poolClinicsMap]);

  // Handlers — search
  const handleSearchChange = (value: string) => {
    setSearchValue(value);
  };

  // Handlers — pagination
  const handlePageChange = (page: number) => {
    setPageIndex(page - 1);
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(parseInt(value, 10));
    setPageIndex(0);
  };

  // Handlers — open create pool modal
  const handleAddRow = () => {
    setEditingKey(null);
    setEditValues(null);
    setCreateModalOpen(true);
  };

  // Handlers — close create pool modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  // Handlers — submit create pool modal
  const handleCreateCallPool = async (values: CreateCallPoolRequest) => {
    if (!values.title.trim()) return;

    const clinicIds = values.clinicIds ?? [];
    const selectedClinics = clinics.filter((c) => clinicIds.includes(c.id));

    const result = await dispatch(
      createCallPool({
        title: values.title.trim(),
        channelsCount: values.channelsCount,
        clinicIds,
      }),
    );
    if (createCallPool.fulfilled.match(result)) {
      const newPoolId = result.payload;
      setCreateModalOpen(false);
      await dispatch(
        searchCallPools({
          TextSearch: debouncedSearch,
          PageIndex: pageIndex,
          PageSize: pageSize,
        }),
      );
      dispatch(
        updatePoolClinicsAfterSave({
          poolId: newPoolId,
          clinics: selectedClinics,
        }),
      );
    }
  };

  // Handlers — start editing existing row
  const handleStartEdit = (record: EditableRow) => {
    setCreateModalOpen(false);
    setEditingKey(record.key);
    setEditValues({
      title: record.title,
      channelsCount: record.channelsCount,
      clinics: record.clinics,
    });
  };

  // Handlers — cancel edit
  const handleCancelEdit = () => {
    setEditingKey(null);
    setEditValues(null);
  };

  const handleUpdateCallPool = async (values: CreateCallPoolRequest) => {
    if (!editingKey || !values.title.trim()) return;

    const clinicIds = values.clinicIds ?? [];
    const selectedClinics = clinics.filter((c) => clinicIds.includes(c.id));
    const poolId = editingKey;
    const result = await dispatch(
      updateCallPool({
        callPoolId: poolId,
        data: {
          title: values.title.trim(),
          channelsCount: values.channelsCount,
          clinicIds,
        },
      }),
    );

    if (updateCallPool.fulfilled.match(result)) {
      handleCancelEdit();
      await dispatch(
        searchCallPools({
          TextSearch: debouncedSearch,
          PageIndex: pageIndex,
          PageSize: pageSize,
        }),
      );
      dispatch(
        updatePoolClinicsAfterSave({ poolId, clinics: selectedClinics }),
      );
    }
  };

  const editModalInitialValues = editValues
    ? {
        title: editValues.title,
        channelsCount: editValues.channelsCount,
        clinicIds: editValues.clinics.map((clinic) => clinic.id),
      }
    : undefined;

  return {
    // Data
    items: enrichedItems,
    totalItemsCount,
    allClinics,
    clinics,
    poolClinicsLoading,
    status,
    saving,

    // Search & pagination
    searchValue,
    pageIndex,
    pageSize,
    handleSearchChange,
    handlePageChange,
    handlePageSizeChange,

    // Editing
    editingKey,
    editValues,
    editModalOpen: editingKey !== null,
    editModalInitialValues,
    handleAddRow,
    handleStartEdit,
    handleCancelEdit,
    handleUpdateCallPool,

    // Create pool modal
    createModalOpen,
    handleCloseCreateModal,
    handleCreateCallPool,
  };
};
